import cx from 'classnames';
import React from 'react';

import { Button, ButtonGroup, Input, Spinner } from '@flixbus/honeycomb-react';

import Scroller from 'common/Scroller';
import {
	COL_WIDTH,
	totalColsFromStations,
} from 'pages/WorkAssignments/components/WorkAssignmentCard/utils';

import { EDITOR_TEXTS } from './texts';
import { useStationEditor } from './useStationEditor';

import styles from './styles.module.css';

const SCROLLER_STYLES = {
	fade: false,
	navWrapLeft: { left: '-42px' },
	navWrapRight: { right: '-42px' },
	navLeft: { width: '32px', height: '32px', boxShadow: 'none' },
	navRight: { width: '32px', height: '32px', boxShadow: 'none' },
};

const StationEditor = ({
	sequences,
	stations,
	stationActuals,
	sequenceGroupId,
	firstStation,
	lastStation,
	onSaveSuccess,
	onCancel,
}) => {
	const {
		values,
		timeOrderError,
		isDirty,
		isSaving,
		fieldsByStation,
		handleChange,
		handleCancel,
		handleSave,
	} = useStationEditor({
		sequences,
		stations,
		stationActuals,
		sequenceGroupId,
		firstStation,
		lastStation,
		onSaveSuccess,
		onCancel,
	});

	const totalCols = totalColsFromStations(stations.length);
	const gridTemplateColumns = `repeat(${totalCols}, minmax(${COL_WIDTH}px, 1fr))`;

	if (isSaving) {
		return (
			<div role="status" className={styles.savingStatus}>
				<Spinner size="lg" />
				<span className={styles.srOnly}>{EDITOR_TEXTS.saving}</span>
			</div>
		);
	}

	return (
		<div className={styles.editorWrap}>
			<div className={styles.stationsRow}>
				<Scroller scrollStep={COL_WIDTH * 2} styles={SCROLLER_STYLES}>
					<div className={styles.grid} style={{ gridTemplateColumns }}>
						{stations.map((station, idx) => {
							const isFirst = idx === 0;
							const isLast = idx === stations.length - 1;
							const fields = fieldsByStation[idx] ?? {};
							const colSpan = isFirst || isLast ? 1 : 2;
							const hasArrivalError = timeOrderError?.fieldKey === `${idx}:arrival`;
							const hasDepartureError = timeOrderError?.fieldKey === `${idx}:departure`;
							const showDivider =
								!isFirst &&
								!isLast &&
								fields.arrival !== undefined &&
								fields.departure !== undefined;

							return (
								<div
									key={station.id}
									className={cx(styles.stationCell, {
										[styles.stationCellFirst]: isFirst,
										[styles.stationCellLast]: isLast,
									})}
									style={{ gridColumn: `span ${colSpan}` }}
								>
									<div className={styles.code}>{station.code}</div>
									<div className={styles.name}>{station.name}</div>

									<div className={styles.timeFields}>
										{!isFirst && fields.arrival !== undefined && (
											<div className={styles.timeInputWrap}>
												<Input
													id={`station-${idx}-arrival`}
													type="time"
													aria-label={EDITOR_TEXTS.arrivalLabel(station.code)}
													value={values[fields.arrival]}
													valid={hasArrivalError ? false : undefined}
													infoError={hasArrivalError ? timeOrderError.message : undefined}
													data-testid={`station-${idx}-arrival`}
													onChange={(e) => handleChange(fields.arrival, e.target.value)}
												/>
											</div>
										)}

										{showDivider && <div className={styles.divider} />}

										{!isLast && fields.departure !== undefined && (
											<div className={styles.timeInputWrap}>
												<Input
													id={`station-${idx}-departure`}
													type="time"
													aria-label={EDITOR_TEXTS.departureLabel(station.code)}
													value={values[fields.departure]}
													valid={hasDepartureError ? false : undefined}
													infoError={hasDepartureError ? timeOrderError.message : undefined}
													data-testid={`station-${idx}-departure`}
													onChange={(e) => handleChange(fields.departure, e.target.value)}
												/>
											</div>
										)}
									</div>
								</div>
							);
						})}
					</div>
				</Scroller>
			</div>

			<ButtonGroup justify="center">
				<Button appearance="secondary" onClick={handleCancel} data-testid="editor-cancel-btn">
					{EDITOR_TEXTS.cancel}
				</Button>
				<Button
					appearance="primary"
					onClick={handleSave}
					disabled={!isDirty || timeOrderError !== null}
					data-testid="editor-save-btn"
				>
					{EDITOR_TEXTS.saveChanges}
				</Button>
			</ButtonGroup>
		</div>
	);
};

export default StationEditor;
